import { FocusMonitor } from '@angular/cdk/a11y';
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    HOST_TAG_NAME,
    OnDestroy,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    contentChildren,
    inject,
    input,
    signal,
} from '@angular/core';

@Directive({
    selector: 'a[dsBottomNavTab], ds-bottom-nav-tab',
    standalone: true,
    host: {
        'class': 'ds-bottom-nav-item',
        '[class.ds-bottom-nav-selected]': 'active()',
        '[class.ds-bottom-nav-disabled]': 'disabled()',
        '[class.ds-bottom-nav-focused]': 'focused',
        '[attr.aria-disabled]': 'ariaDisabled()',
        '[attr.aria-current]': 'ariaCurrent()',
        '[attr.tabindex]': '!disabled() ? 0 : -1',
        '[attr.role]': 'role()',
    },
})
export class DsBottomNavTab {
    public readonly disabled = input(false, { transform: booleanAttribute });
    public readonly active = input(false, { transform: booleanAttribute });
    protected readonly focused = signal(false);
    public readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly hostTagName = inject(HOST_TAG_NAME);
    private readonly isAnchor = this.hostTagName.toLowerCase() === 'a';

    // custom elements get role="button"
    protected readonly role = computed(() => {
        return !this.isAnchor ? 'button' : null;
    });

    // Only set aria-disabled when actually disabled
    protected readonly ariaDisabled = computed(() => {
        return this.disabled() ? true : null;
    });

    // aria-current="page" for all active items
    protected readonly ariaCurrent = computed(() => {
        return this.active() ? 'page' : null;
    });

    focus() {
        this.elementRef.nativeElement.focus();
        this.focused.set(true);
        this.cdr.markForCheck();
    }

    blur() {
        this.focused.set(false);
        this.cdr.markForCheck();
    }
}

@Component({
    selector: 'ds-bottom-nav',
    standalone: true,
    host: {
        'class': 'ds-bottom-nav',
        '(keydown)': 'onKeydown($event)',
    },
    template: ` <div class="ds-bottom-nav-container" role="navigation" aria-label="Bottom navigation">
        <div class="ds-bottom-nav-items">
            <ng-content />
        </div>
    </div>`,
    styleUrls: ['./bottom-nav.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsBottomNav implements AfterContentInit, OnDestroy {
    protected readonly tabs = contentChildren(DsBottomNavTab);
    private readonly _focusMonitor = inject(FocusMonitor);

    ngAfterContentInit() {
        this.tabs().forEach((tab) => {
            this._focusMonitor.monitor(tab.elementRef);
        });
    }

    onKeydown(event: KeyboardEvent): void {
        const { key } = event;

        if (key === 'Enter' || key === ' ') {
            let tabsList = this.tabs();
            let focusedIndex = tabsList.findIndex((tab) => tab.elementRef.nativeElement.classList.contains('cdk-focused'));

            const focusedTab = tabsList[focusedIndex];
            if (focusedTab && !focusedTab.disabled()) {
                tabsList[focusedIndex]?.elementRef.nativeElement.click();
                event.preventDefault();
            }
        }
    }
    ngOnDestroy() {
        if (this.tabs()) {
            this.tabs().forEach((tab) => {
                this._focusMonitor.stopMonitoring(tab.elementRef);
            });
        }
    }
}
